import React, {useState} from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import {connect} from 'react-redux'
import actions from 'redux-auto'
import {isReadonly, isSubmitted, isDraft, isConfirmed} from 'ag-common/tools/fileStatus'
import {findNestedValue} from 'ag-common/tools/objects'
import Headline from '#components/a/Headline'
import Button from '#components/a/Button'
import ControlsGroup from '#components/m/ControlsGroup'
import Status from '#components/a/Status'
import AuthorInfo from '#components/m/AuthorInfo'
import EinreichenButton from './EinreichenButton'
import AblehnenButton from './AblehnenButton'
import FreigebenButton from './FreigebenButton'
import FreigabeZurueckziehenButton from './FreigabeZurueckziehenButton'
import Api from '#services/api'
import Loading from '#components/a/Loading'
import ErrorModal from '#components/m/ErrorModal'

export class AusbildungsplanContentHeader extends React.Component {
  constructor() {
    super()
    this.state = {
      isLoading: false,
      error: null,
    }
  }

  isLoading = () => {
    return this.state.isLoading
  }

  isError = () => {
    return this.state.error
  }

  canVerify = () => {
    const {permissions, chapterStatus} = this.props
    return permissions.ausbildungsplan.verify && isSubmitted(chapterStatus)
  }

  canZurueckziehen = () => {
    const {permissions, chapterStatus} = this.props
    return permissions.ausbildungsplan.verify && isConfirmed(chapterStatus)
  }

  canEinreichen = () => {
    const {permissions, chapterStatus} = this.props
    return permissions.ausbildungsplan.edit && !isReadonly(chapterStatus)
  }

  statusText = () => {
    const {chapterStatus, questionsLength, finishedAnswersCount, permissions} = this.props
    if (isDraft(chapterStatus)) {
      return permissions.ausbildungsplan.edit
        ? i18n.t('question_progress', [finishedAnswersCount, questionsLength])
        : i18n.t('misc_status_preview')
    } else {
      return chapterStatus
    }
  }

  renderStatus = () => {
    const {chapterStatus} = this.props
    const statusText = this.statusText()
    return <Status status={chapterStatus} text={statusText} useGreyDraft={true} />
  }

  renderSavingAndAuthorInfo = () => {
    const {isSaving, chapterStatus, chapterLastStatusChange, permissions, azubiId, hasChanges, savingError} = this.props

    return (
      <AuthorInfo
        status={chapterStatus}
        lastStatusChange={chapterLastStatusChange}
        isAuthor={permissions.ausbildungsplan.edit}
        isSaving={isSaving}
        hasChanges={hasChanges}
        error={savingError}
        userId={azubiId}
      />
    )
  }

  performEinreichen = () => {
    const {chapterId, onEinreichen, tryValidateAnswers} = this.props

    if (tryValidateAnswers()) {
      onEinreichen(chapterId)
    }
  }

  handlePrintButton = async () => {
    this.setState({isLoading: true})
    await this.handlePrint()
    this.setState({isLoading: false})
  }

  handlePrint = async () => {
    try {
      const {azubiId} = this.props
      return await Api.printAusbildungsplan(azubiId)
    } catch (e) {
      if (e && e.message) {
        const translatedMessage = i18n.t(e.message)
        if (translatedMessage !== e.message) {
          e.message = translatedMessage
        }
        this.setState({error: e})
      } else {
        actions.error.writeError({error: e})
        throw new Error(e.message)
      }
    }
  }

  render() {
    const {
      azubiId,
      chapterId,
      title,
      nextButtonClicked,
      lastQuestion,
      onAblehnen,
      onFreigeben,
      questionsLength,
      finishedAnswersCount,
      hasComment,
      onFreigabeZurueckziehen,
    } = this.props

    const hasAnsweredAll = finishedAnswersCount >= questionsLength
    return (
      <>
        {this.isError() && this.state.error.message && (
          <ErrorModal
            headline={i18n.t('ausbildungsnachweis_print_error')}
            text={this.state.error.message}
            error={this.state.error}
            showModal={true}
            buttonText={i18n.t('okay')}
            onButtonClick={() => {
              this.setState({error: null})
            }}
          />
        )}
        <div className="ausbildungsplan__header">
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8">
              <Headline modifier="section" importance={2} text={title} />
              {this.renderStatus()}
              {this.renderSavingAndAuthorInfo()}
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4">
              <ControlsGroup modifier="wrapper">
                <ControlsGroup modifier="secondary">
                  <div style={{display: 'flex', justifyContent: 'center', width: '130px'}}>
                    {this.isLoading() && <Loading />}
                    {!this.isLoading() && (
                      <Button
                        text={i18n.t('export_print_buttontext')}
                        variant="secondary"
                        modifiers={['print']}
                        testid="print-button"
                        event={this.handlePrintButton}
                      />
                    )}
                  </div>
                </ControlsGroup>
                <ControlsGroup modifier="secondary">
                  <Button
                    text={i18n.t('ausbildungsplan_next_question')}
                    event={nextButtonClicked}
                    variant="secondary"
                    disabled={lastQuestion}
                  />
                </ControlsGroup>
                <ControlsGroup>
                  {this.canEinreichen() && (
                    <EinreichenButton submit={this.performEinreichen} disabled={!hasAnsweredAll} />
                  )}
                  {this.canVerify() && (
                    <AblehnenButton ablehnen={() => onAblehnen(azubiId, chapterId)} disabled={!hasComment} />
                  )}
                  {this.canVerify() && <FreigebenButton freigeben={() => onFreigeben(azubiId, chapterId)} />}
                  {this.canZurueckziehen() && (
                    <FreigabeZurueckziehenButton
                      freigabeZurueckziehen={() => onFreigabeZurueckziehen(azubiId, chapterId)}
                    />
                  )}
                </ControlsGroup>
              </ControlsGroup>
            </div>
          </div>
        </div>
      </>
    )
  }
}

AusbildungsplanContentHeader.propTypes = {
  title: PropTypes.string.isRequired,
  azubiId: PropTypes.number.isRequired,
  chapterId: PropTypes.string.isRequired,
  nextButtonClicked: PropTypes.func,
  lastQuestion: PropTypes.bool,
  questionsLength: PropTypes.number.isRequired,
  finishedAnswersCount: PropTypes.number,
  chapterStatus: PropTypes.string.isRequired,
  chapterLastStatusChange: PropTypes.string,
  onEinreichen: PropTypes.func.isRequired,
  onAblehnen: PropTypes.func.isRequired,
  onFreigeben: PropTypes.func.isRequired,
  onFreigabeZurueckziehen: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  hasComment: PropTypes.bool,
  hasChanges: PropTypes.bool.isRequired,
  savingError: PropTypes.string,
  tryValidateAnswers: PropTypes.func.isRequired,
  permissions: PropTypes.object,
}

const mapStateToProps = (state, props) => {
  const {chapterId, azubiId} = props
  const chapterAnswers = state.ausbildungsplan.answers[chapterId]
  return {
    finishedAnswersCount: chapterAnswers ? chapterAnswers.finishedAnswersCount : 0,
    chapterLastStatusChange: findNestedValue(state, [
      'ausbildungsplan',
      'chapterStatuses',
      azubiId,
      chapterId,
      'lastStatusChange',
    ]),
    isSaving: state.ausbildungsplan.isSaving,
    savingError: state.ausbildungsplan.errorSavingAnswer,
    permissions: state.authentication.permissions,
  }
}

function mapDispatchToProps() {
  return {
    onEinreichen: chapterId => actions.ausbildungsplan.handleChapterStatus({chapterId, statusChange: 'Einreichen'}),
    onFreigeben: (azubiId, chapterId) =>
      actions.ausbildungsplan.handleChapterStatus({azubiId, chapterId, statusChange: 'Freigeben'}),
    onAblehnen: (azubiId, chapterId) =>
      actions.ausbildungsplan.handleChapterStatus({azubiId, chapterId, statusChange: 'Ablehnen'}),
    onFreigabeZurueckziehen: (azubiId, chapterId) =>
      actions.ausbildungsplan.handleChapterStatus({azubiId, chapterId, statusChange: 'Zurueckziehen'}),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AusbildungsplanContentHeader)
